import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { manageAISessions, manageTrainingRecords } from '../lib/api';
import { User, BookOpen, Brain, Target, Calendar, Award, TrendingUp, Eye, X } from 'lucide-react';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';

interface StudyRecord {
  id: string;
  model_name: string;
  notes: string;
  created_at: string;
}

interface TrainingRecord {
  id: string;
  question_title: string;
  question_content: string;
  text_answer: string;
  ai_reference_answer: string;
  score: number;
  ai_evaluation: string;
  created_at: string;
}

const Profile: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('study');
  const [studyRecords, setStudyRecords] = useState<StudyRecord[]>([]);
  const [trainingRecords, setTrainingRecords] = useState<TrainingRecord[]>([]);
  const [selectedTrainingRecord, setSelectedTrainingRecord] = useState<TrainingRecord | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchUserRecords();
    }
  }, [user]);

  const fetchUserRecords = async () => {
    try {
      // 使用edge function获取模型学习记录
      const modelChatsResponse = await fetch(`${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/user-learning-unified-manage?module=model-chats&action=list&limit=50`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!modelChatsResponse.ok) {
        throw new Error('获取模型聊天记录失败');
      }

      const modelChatsData = await modelChatsResponse.json();
      
      if (!modelChatsData.success) {
        throw new Error(modelChatsData.error || '获取模型聊天记录失败');
      }

      setStudyRecords(modelChatsData.data.data || []);

      // 获取训练记录
      const trainingResponse = await manageTrainingRecords('list', {
        fields: ['id', 'text_answer', 'score', 'ai_evaluation', 'created_at'],
        order: { created_at: 'desc' },
        include_relations: ['training_questions', 'contents']
      });

      if (!trainingResponse.success) {
        throw new Error(trainingResponse.error || '获取训练记录失败');
      }

      // Process training records with content data
      const formattedTrainingRecords = (trainingResponse.data?.data || []).map((record: any) => {
        const question = record.training_questions;
        const content = record.contents;
        
        let questionContent = question?.title || ''; // Fallback to title

        if (content?.content_data) {
          questionContent = content.content_data;
        }

        return {
          id: record.id,
          question_title: question?.title || '未知题目',
          question_content: questionContent,
          text_answer: record.text_answer,
          ai_reference_answer: question?.ai_reference_answer || '',
          score: record.score,
          ai_evaluation: record.ai_evaluation,
          created_at: record.created_at,
        };
      });

      setTrainingRecords(formattedTrainingRecords);
    } catch (error) {
      console.error('获取用户记录失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = {
    totalStudyRecords: studyRecords.length,
    totalTrainingRecords: trainingRecords.length,
    averageScore: trainingRecords.length > 0 
      ? Math.round(trainingRecords.reduce((sum, record) => sum + record.score, 0) / trainingRecords.length)
      : 0,
    thisMonthTrainings: trainingRecords.filter(record => {
      const recordDate = new Date(record.created_at);
      const now = new Date();
      return recordDate.getMonth() === now.getMonth() && recordDate.getFullYear() === now.getFullYear();
    }).length,
  };

  const tabs = [
    { id: 'study', name: '模型学习', icon: BookOpen, count: stats.totalStudyRecords },
    { id: 'training', name: '思维训练', icon: Brain, count: stats.totalTrainingRecords },
    { id: 'expression', name: '表达训练', icon: Target, count: 0 },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-white" />
            </div>
            <div className="ml-6">
              <h1 className="text-2xl font-bold text-gray-900">{user?.email}</h1>
              <p className="text-gray-600">学习进度和成长轨迹</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{stats.totalStudyRecords}</p>
                <p className="text-sm text-gray-600">学习记录</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Brain className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{stats.totalTrainingRecords}</p>
                <p className="text-sm text-gray-600">训练次数</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Award className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{stats.averageScore}</p>
                <p className="text-sm text-gray-600">平均分数</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-2xl font-bold text-gray-900">{stats.thisMonthTrainings}</p>
                <p className="text-sm text-gray-600">本月训练</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-5 h-5 mr-2" />
                    {tab.name}
                    <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'study' && (
              <div className="space-y-4">
                {studyRecords.length > 0 ? (
                  studyRecords.map((record) => (
                    <div key={record.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-medium text-gray-900">{record.model_name}</h3>
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar className="w-4 h-4 mr-1" />
                          {format(new Date(record.created_at), 'yyyy-MM-dd HH:mm')}
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm whitespace-pre-wrap">{record.notes}</p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">暂无学习记录</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'training' && (
              <div className="space-y-4">
                {trainingRecords.length > 0 ? (
                  trainingRecords.map((record) => (
                    <div key={record.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-medium text-gray-900">{record.question_title}</h3>
                        <div className="flex items-center space-x-4">
                          <button
                            onClick={() => setSelectedTrainingRecord(record)}
                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                            title="查看详情"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                            record.score >= 90 ? 'bg-green-100 text-green-800' :
                            record.score >= 80 ? 'bg-blue-100 text-blue-800' :
                            record.score >= 70 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {record.score}分
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar className="w-4 h-4 mr-1" />
                            {format(new Date(record.created_at), 'yyyy-MM-dd HH:mm')}
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm whitespace-pre-wrap line-clamp-3">
                        {record.ai_evaluation}
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Brain className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">暂无训练记录</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'expression' && (
              <div className="text-center py-8">
                <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">表达训练功能即将上线</p>
              </div>
            )}
          </div>
        </div>

        {/* Training Record Detail Modal */}
        {selectedTrainingRecord && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    训练记录详情
                  </h2>
                  <button
                    onClick={() => setSelectedTrainingRecord(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* 题目信息 */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">题目</h3>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">
                        {selectedTrainingRecord.question_title}
                      </h4>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap">
                        {selectedTrainingRecord.question_content}
                      </div>
                    </div>
                  </div>

                  {/* 我的答案 */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">我的答案</h3>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="text-blue-800 whitespace-pre-wrap">
                        {selectedTrainingRecord.text_answer}
                      </div>
                    </div>
                  </div>

                  {/* AI评价 */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2 flex items-center">
                      AI评价
                      <span className={`ml-2 px-2 py-1 rounded-full text-sm font-medium ${
                        selectedTrainingRecord.score >= 90 ? 'bg-green-100 text-green-800' :
                        selectedTrainingRecord.score >= 80 ? 'bg-blue-100 text-blue-800' :
                        selectedTrainingRecord.score >= 70 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {selectedTrainingRecord.score}分
                      </span>
                    </h3>
                    <div className="p-4 bg-purple-50 rounded-lg prose max-w-none">
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => <p className="mb-3 last:mb-0 text-purple-800 leading-relaxed">{children}</p>,
                          ul: ({ children }) => <ul className="list-disc list-inside mb-3 space-y-1 text-purple-800">{children}</ul>,
                          ol: ({ children }) => <ol className="list-decimal list-inside mb-3 space-y-1 text-purple-800">{children}</ol>,
                          li: ({ children }) => <li className="text-purple-800">{children}</li>,
                          h1: ({ children }) => <h1 className="text-xl font-bold mb-3 text-purple-900">{children}</h1>,
                          h2: ({ children }) => <h2 className="text-lg font-bold mb-3 text-purple-900">{children}</h2>,
                          h3: ({ children }) => <h3 className="text-base font-bold mb-2 text-purple-900">{children}</h3>,
                          strong: ({ children }) => <strong className="font-semibold text-purple-900">{children}</strong>,
                          em: ({ children }) => <em className="italic text-purple-800">{children}</em>,
                          code: ({ children }) => <code className="bg-purple-200 px-2 py-1 rounded text-sm font-mono text-purple-900">{children}</code>,
                          pre: ({ children }) => <pre className="bg-purple-200 p-3 rounded text-sm overflow-x-auto mb-3">{children}</pre>,
                          blockquote: ({ children }) => <blockquote className="border-l-4 border-purple-400 pl-4 italic mb-3 text-purple-700">{children}</blockquote>
                        }}
                      >
                        {selectedTrainingRecord.ai_evaluation}
                      </ReactMarkdown>
                    </div>
                  </div>

                  {/* 参考答案 */}
                  {selectedTrainingRecord.ai_reference_answer && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">参考答案</h3>
                      <div className="p-4 bg-green-50 rounded-lg prose max-w-none">
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <p className="mb-3 last:mb-0 text-green-800 leading-relaxed">{children}</p>,
                            ul: ({ children }) => <ul className="list-disc list-inside mb-3 space-y-1 text-green-800">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal list-inside mb-3 space-y-1 text-green-800">{children}</ol>,
                            li: ({ children }) => <li className="text-green-800">{children}</li>,
                            h1: ({ children }) => <h1 className="text-xl font-bold mb-3 text-green-900">{children}</h1>,
                            h2: ({ children }) => <h2 className="text-lg font-bold mb-3 text-green-900">{children}</h2>,
                            h3: ({ children }) => <h3 className="text-base font-bold mb-2 text-green-900">{children}</h3>,
                            strong: ({ children }) => <strong className="font-semibold text-green-900">{children}</strong>,
                            em: ({ children }) => <em className="italic text-green-800">{children}</em>,
                            code: ({ children }) => <code className="bg-green-200 px-2 py-1 rounded text-sm font-mono text-green-900">{children}</code>,
                            pre: ({ children }) => <pre className="bg-green-200 p-3 rounded text-sm overflow-x-auto mb-3">{children}</pre>,
                            blockquote: ({ children }) => <blockquote className="border-l-4 border-green-400 pl-4 italic mb-3 text-green-700">{children}</blockquote>,
                          }}
                        >
                          {selectedTrainingRecord.ai_reference_answer}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}

                  {/* 训练时间 */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">训练时间</h3>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center text-gray-700">
                        <Calendar className="w-4 h-4 mr-2" />
                        {format(new Date(selectedTrainingRecord.created_at), 'yyyy-MM-dd HH:mm')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-6 border-t border-gray-200 mt-6">
                  <button
                    onClick={() => setSelectedTrainingRecord(null)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    关闭
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;